import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Switch,
  FormControlLabel,
  Paper,
  Divider,
  InputAdornment,
  Alert,
  Chip,
} from '@mui/material';
import {
  Close,
  AccessTime,
  VolumeUp,
  AspectRatio,
  Transform,
  PlayArrow,
  Save,
  Restore,
  Info,
} from '@mui/icons-material';

import type { PlaylistItem, Media } from '../../types';
import { MediaType } from '../../types';
import { apiClient } from '../../services/api';

interface PlaylistItemSettingsDialogProps {
  open: boolean;
  item: PlaylistItem | null;
  playlist: { id: number } | null;
  onClose: () => void;
  onSaved: () => void;
}

interface ItemSettings {
  custom_duration: number | null;
  start_time: number | null;
  end_time: number | null;
  volume: number | null;
  fit_mode: string;
  transition_type: string;
  transition_duration: number;
}

const PlaylistItemSettingsDialog: React.FC<PlaylistItemSettingsDialogProps> = ({
  open,
  item,
  playlist,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<ItemSettings>({
    custom_duration: null,
    start_time: null,
    end_time: null,
    volume: null,
    fit_mode: 'fill',
    transition_type: 'fade',
    transition_duration: 0.5,
  });
  const [saving, setSaving] = useState(false);
  const [useCustomDuration, setUseCustomDuration] = useState(false);
  const [useVideoClip, setUseVideoClip] = useState(false);
  const [useCustomVolume, setUseCustomVolume] = useState(false);

  useEffect(() => {
    if (open && item) {
      setSettings({
        custom_duration: item.custom_duration,
        start_time: item.start_time,
        end_time: item.end_time,
        volume: item.volume,
        fit_mode: item.fit_mode || 'fill',
        transition_type: item.transition_type || 'fade',
        transition_duration: item.transition_duration || 0.5,
      });
      setUseCustomDuration(!!item.custom_duration);
      setUseVideoClip(!!(item.start_time || item.end_time));
      setUseCustomVolume(!!item.volume);
    }
  }, [open, item]);

  const handleSave = async () => {
    if (!item || !playlist) return;

    setSaving(true);
    try {
      const updateData = {
        ...settings,
        custom_duration: useCustomDuration ? settings.custom_duration : null,
        start_time: useVideoClip ? settings.start_time : null,
        end_time: useVideoClip ? settings.end_time : null,
        volume: useCustomVolume ? settings.volume : null,
      };

      await apiClient.request('PUT', `/playlists/${playlist.id}/items/${item.id}`, updateData);
      onSaved();
      onClose();
    } catch (error) {
      console.error('Ошибка сохранения настроек элемента:', error);
      // TODO: Показать уведомление об ошибке
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!item) return;
    
    setSettings({
      custom_duration: null,
      start_time: null,
      end_time: null,
      volume: null,
      fit_mode: 'fill',
      transition_type: 'fade',
      transition_duration: 0.5,
    });
    setUseCustomDuration(false);
    setUseVideoClip(false);
    setUseCustomVolume(false);
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getEffectiveDuration = () => {
    if (useCustomDuration && settings.custom_duration) {
      return settings.custom_duration;
    }
    if (useVideoClip && settings.start_time !== null && settings.end_time !== null) {
      return settings.end_time - settings.start_time;
    }
    if (item?.media?.duration) {
      return item.media.duration;
    }
    return item?.media?.media_type === MediaType.IMAGE ? 10 : 0;
  };

  const isVideoMedia = item?.media?.media_type === MediaType.VIDEO;
  const isAudioMedia = item?.media?.media_type === MediaType.AUDIO || isVideoMedia;
  const mediaDuration = item?.media?.duration || 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {t('playlists.itemSettings')}
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Информация о медиафайле */}
        {item?.media && (
          <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              {item.media.title || item.media.filename}
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip size="small" label={item.media.media_type} />
              {item.media.duration && (
                <Chip size="small" label={formatDuration(item.media.duration)} />
              )}
              <Chip size="small" label={`Порядок: ${item.order_index + 1}`} />
            </Box>
          </Paper>
        )}

        <Grid container spacing={3}>
          {/* Настройки длительности */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              <AccessTime sx={{ mr: 1, verticalAlign: 'middle' }} />
              {t('playlists.durationSettings')}
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={useCustomDuration}
                  onChange={(e) => setUseCustomDuration(e.target.checked)}
                />
              }
              label={t('playlists.useCustomDuration')}
            />

            {useCustomDuration && (
              <Box mt={2}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('playlists.duration')}
                  value={settings.custom_duration || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    custom_duration: parseFloat(e.target.value) || null
                  })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">сек</InputAdornment>,
                  }}
                  inputProps={{ min: 0.1, step: 0.1 }}
                />
              </Box>
            )}

            <Alert severity="info" sx={{ mt: 1 }}>
              {t('playlists.effectiveDuration')}: {formatDuration(getEffectiveDuration())}
            </Alert>
          </Grid>

          {/* Настройки обрезки видео */}
          {isVideoMedia && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                <Transform sx={{ mr: 1, verticalAlign: 'middle' }} />
                {t('playlists.videoClipping')}
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={useVideoClip}
                    onChange={(e) => setUseVideoClip(e.target.checked)}
                  />
                }
                label={t('playlists.useVideoClip')}
              />

              {useVideoClip && (
                <Box mt={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label={t('playlists.startTime')}
                        value={settings.start_time || ''}
                        onChange={(e) => setSettings({
                          ...settings,
                          start_time: parseFloat(e.target.value) || null
                        })}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">сек</InputAdornment>,
                        }}
                        inputProps={{ min: 0, max: mediaDuration, step: 0.1 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label={t('playlists.endTime')}
                        value={settings.end_time || ''}
                        onChange={(e) => setSettings({
                          ...settings,
                          end_time: parseFloat(e.target.value) || null
                        })}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">сек</InputAdornment>,
                        }}
                        inputProps={{ min: 0, max: mediaDuration, step: 0.1 }}
                      />
                    </Grid>
                  </Grid>
                  {mediaDuration > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {t('playlists.originalDuration')}: {formatDuration(mediaDuration)}
                    </Typography>
                  )}
                </Box>
              )}
            </Grid>
          )}

          {/* Настройки громкости */}
          {isAudioMedia && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                <VolumeUp sx={{ mr: 1, verticalAlign: 'middle' }} />
                {t('playlists.volumeSettings')}
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={useCustomVolume}
                    onChange={(e) => setUseCustomVolume(e.target.checked)}
                  />
                }
                label={t('playlists.useCustomVolume')}
              />

              {useCustomVolume && (
                <Box mt={2}>
                  <Typography gutterBottom>
                    {t('playlists.volume')}: {Math.round((settings.volume || 1) * 100)}%
                  </Typography>
                  <Slider
                    value={settings.volume || 1}
                    onChange={(e, value) => setSettings({
                      ...settings,
                      volume: value as number
                    })}
                    min={0}
                    max={1}
                    step={0.1}
                    marks={[
                      { value: 0, label: '0%' },
                      { value: 0.5, label: '50%' },
                      { value: 1, label: '100%' },
                    ]}
                  />
                </Box>
              )}
            </Grid>
          )}

          {/* Режим отображения */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              <AspectRatio sx={{ mr: 1, verticalAlign: 'middle' }} />
              {t('playlists.displayMode')}
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel>{t('playlists.fitMode')}</InputLabel>
              <Select
                value={settings.fit_mode}
                label={t('playlists.fitMode')}
                onChange={(e) => setSettings({
                  ...settings,
                  fit_mode: e.target.value
                })}
              >
                <MenuItem value="fill">{t('playlists.fitModes.fill')}</MenuItem>
                <MenuItem value="fit">{t('playlists.fitModes.fit')}</MenuItem>
                <MenuItem value="stretch">{t('playlists.fitModes.stretch')}</MenuItem>
                <MenuItem value="crop">{t('playlists.fitModes.crop')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Настройки переходов */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              <Transform sx={{ mr: 1, verticalAlign: 'middle' }} />
              {t('playlists.transitions')}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={8}>
                <FormControl fullWidth>
                  <InputLabel>{t('playlists.transitionType')}</InputLabel>
                  <Select
                    value={settings.transition_type}
                    label={t('playlists.transitionType')}
                    onChange={(e) => setSettings({
                      ...settings,
                      transition_type: e.target.value
                    })}
                  >
                    <MenuItem value="fade">{t('playlists.transitionTypes.fade')}</MenuItem>
                    <MenuItem value="slide">{t('playlists.transitionTypes.slide')}</MenuItem>
                    <MenuItem value="dissolve">{t('playlists.transitionTypes.dissolve')}</MenuItem>
                    <MenuItem value="none">{t('playlists.transitionTypes.none')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('playlists.transitionDuration')}
                  value={settings.transition_duration}
                  onChange={(e) => setSettings({
                    ...settings,
                    transition_duration: parseFloat(e.target.value) || 0.5
                  })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">сек</InputAdornment>,
                  }}
                  inputProps={{ min: 0, max: 5, step: 0.1 }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button startIcon={<Restore />} onClick={handleReset}>
          {t('common.reset')}
        </Button>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? t('common.saving') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlaylistItemSettingsDialog; 